import { useEffect, useMemo, useRef, useState } from 'react';
import { useGetIdentity, useNotify, useRefresh } from 'react-admin';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { getToken, getRefreshToken } from '../authentication/authProvider';
import { EmailHtmlEditor } from './EmailHtmlEditor';

const API_URL = process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001';
const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;

type DealStage = 'at_need' | 'pre_need';
type Province = 'quebec' | 'ontario';

const TIMELINES: Record<DealStage, string[]> = {
  at_need: ['Passed', 'Imminent (1-7 days)', 'Near future (7-30 days)', 'Preplanning (30+ days)'],
  pre_need: ['Planning now', 'Follow up'],
};

const RELATIONSHIPS: Array<[string, string]> = [
  ['spouse', 'Spouse/Partner'],
  ['child', 'Child'],
  ['parent', 'Parent'],
  ['sibling', 'Sibling'],
  ['friend', 'Friend'],
  ['other-family', 'Other Family Member'],
  ['legal-rep', 'Legal Representative'],
  ['other', 'Other'],
];

interface SmsPreview {
  enabled: boolean;
  phone: string | null;
  disabledReason: string | null;
  text: string;
  hint: string;
}

async function authedPost(path: string, body: unknown) {
  const token = await getToken();
  const response = await fetch(`${baseUrl}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'Refresh-Token': await getRefreshToken(),
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || `Request failed (${response.status})`);
  }
  return response.json();
}

/** "+15145550100" → "(514) 555-0100". */
export function formatPhone(e164: string | null): string {
  const d = (e164 || '').replace(/\D/g, '');
  const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : e164 || '';
}

function newRequestId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: Array<[T, string]>;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div role="group" aria-label={label} className="flex w-full rounded-md border border-input bg-muted p-0.5">
      {options.map(([v, text]) => (
        <button
          key={v}
          type="button"
          aria-pressed={value === v}
          onClick={() => onChange(v)}
          className={cn(
            'flex-1 rounded px-2 py-1.5 text-sm transition-colors',
            value === v
              ? 'bg-background font-semibold text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor?: string;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div className="grid min-w-0 flex-1 gap-1">
      <label htmlFor={htmlFor} className="text-[13px] font-medium">
        {label}
      </label>
      {children}
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

const selectClass =
  'h-9 w-full rounded-md border border-input bg-background px-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function QualifyLeadDialog({
  open,
  onClose,
  leadId,
  contact,
  lead,
}: {
  open: boolean;
  onClose: () => void;
  leadId: string;
  contact: { first_name?: string; last_name?: string; email?: string; name?: string } | null;
  lead?: any;
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  // The logged-in agent signs the email and the text. Ignore the email
  // fallback (identity.fullName is the email when full_name is unset).
  const senderName = (() => {
    const n = (identity?.fullName || '').toString().trim();
    return n && !n.includes('@') ? n : undefined;
  })();

  const [submitting, setSubmitting] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [language, setLanguage] = useState<'en' | 'fr'>('en');
  const [province, setProvince] = useState<Province>('quebec');
  const [dealStage, setDealStage] = useState<DealStage>('at_need');
  const [timeline, setTimeline] = useState('');
  const [relationship, setRelationship] = useState('');
  const [errors, setErrors] = useState<{ timeline?: string; email?: string }>({});

  const [sendEmail, setSendEmail] = useState(true);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailHtml, setEmailHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewKeyRef = useRef<string | null>(null);

  const [sendSms, setSendSms] = useState(true);
  const [sms, setSms] = useState<SmsPreview | null>(null);
  const [smsText, setSmsText] = useState('');
  const smsEditedRef = useRef(false);
  const requestIdRef = useRef(newRequestId());

  // Reset from the contact + lead every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setFirstName(contact?.first_name || '');
    setLastName(contact?.last_name || '');
    setEmail(contact?.email || '');
    setLanguage(lead?.language === 'fr' ? 'fr' : 'en');
    setProvince('quebec');
    setDealStage((lead?.intent || '').includes('pre') ? 'pre_need' : 'at_need');
    setTimeline('');
    setRelationship('');
    setErrors({});
    setSendEmail(true);
    setEmailOpen(false);
    setEmailSubject('');
    setEmailHtml('');
    setPreviewError(null);
    previewKeyRef.current = null;
    setSendSms(true);
    setSms(null);
    setSmsText('');
    smsEditedRef.current = false;
    requestIdRef.current = newRequestId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const emailUsable = sendEmail && EMAIL_RE.test(email.trim());

  // The editable email preview: fetched when its inputs change, not per keystroke.
  useEffect(() => {
    if (!open || !sendEmail || !email.trim()) return;
    const key = `${dealStage}|${language}|${province}|${firstName}|${senderName || ''}`;
    if (previewKeyRef.current === key) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    void (async () => {
      try {
        const data = await authedPost('/leads/quote-email-preview', {
          dealStage,
          language,
          province,
          firstName: firstName.trim() || undefined,
          senderName,
        });
        if (cancelled) return;
        previewKeyRef.current = key;
        setEmailSubject(data.subject || '');
        setEmailHtml(data.html || '');
      } catch (e: any) {
        if (!cancelled) setPreviewError(e.message || 'Failed to load email preview');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sendEmail, dealStage, language, province, firstName, email, senderName]);

  // The text preview follows the choices until staff edit it.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const data: SmsPreview = await authedPost('/qualify-sms/preview', {
            leadId,
            dealStage,
            language,
            firstName: firstName.trim() || undefined,
            senderName,
            withEmail: emailUsable,
            timeline: timeline || undefined,
          });
          if (cancelled) return;
          setSms(data);
          if (!smsEditedRef.current) setSmsText(data.text);
        } catch {
          if (!cancelled) setSms(null);
        }
      })();
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, leadId, dealStage, language, firstName, senderName, emailUsable, timeline]);

  const smsAvailable = !!sms?.enabled && !!sms.phone && !sms.disabledReason;
  const smsShown = !!sms?.enabled;
  const timelineOptions = useMemo(() => TIMELINES[dealStage], [dealStage]);

  const handleSubmit = async () => {
    const next: typeof errors = {};
    if (dealStage === 'at_need' && !timeline) next.timeline = 'Pick a timeline for an at-need lead.';
    if (email.trim() && !EMAIL_RE.test(email.trim())) next.email = 'Check the email address.';
    setErrors(next);
    if (Object.keys(next).length) return;

    setSubmitting(true);
    try {
      const willText = smsAvailable && sendSms && !!smsText.trim();
      const result = await authedPost(`/leads/${leadId}/qualify`, {
        dealStage,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        email: email.trim() || undefined,
        language,
        province,
        // As before the redesign: the pre-need timeline is not stored.
        cremationCategory: dealStage === 'at_need' ? timeline : undefined,
        relationship: dealStage === 'at_need' ? relationship || undefined : undefined,
        sendEmail,
        senderName,
        ...(sendEmail && emailSubject && emailHtml
          ? { customSubject: emailSubject, customHtml: emailHtml }
          : {}),
        sendSms: willText,
        smsText: willText ? smsText.trim() : undefined,
        qualifyRequestId: requestIdRef.current,
      });
      notify(
        willText
          ? result?.smsScheduledId
            ? 'Lead qualified. The text is scheduled.'
            : 'Lead qualified. The text could not be scheduled.'
          : 'Lead qualified',
        { type: willText && !result?.smsScheduledId ? 'warning' : 'success' },
      );
      onClose();
      refresh();
    } catch (e: any) {
      notify(e.message || 'Failed to qualify lead', { type: 'warning' });
    } finally {
      setSubmitting(false);
    }
  };

  const name = [firstName, lastName].filter(Boolean).join(' ') || contact?.name || 'lead';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-[560px] gap-0 overflow-y-auto p-0">
        <DialogHeader className="px-5 pb-1 pt-5">
          <DialogTitle>Qualify {name}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-5 px-5 pb-5 pt-3">
          <section className="grid gap-2.5">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Contact</h3>
            <div className="flex gap-3">
              <Field label="First name" htmlFor="q-first">
                <Input id="q-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </Field>
              <Field label="Last name" htmlFor="q-last">
                <Input id="q-last" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </Field>
            </div>
            <Field label="Email" htmlFor="q-email" error={errors.email}>
              <Input
                id="q-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={cn(errors.email && 'border-destructive')}
              />
            </Field>
            <div className="flex gap-3">
              <Field label="Language">
                <Segmented
                  label="Language"
                  value={language}
                  onChange={setLanguage}
                  options={[['en', 'English'], ['fr', 'Français']]}
                />
              </Field>
              <Field label="Province">
                <Segmented
                  label="Province"
                  value={province}
                  onChange={setProvince}
                  options={[['quebec', 'Quebec'], ['ontario', 'Ontario']]}
                />
              </Field>
            </div>
          </section>

          <section className="grid gap-2.5">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Deal</h3>
            <Segmented
              label="Deal stage"
              value={dealStage}
              onChange={(v) => {
                setDealStage(v);
                setTimeline('');
                setErrors((e) => ({ ...e, timeline: undefined }));
              }}
              options={[['at_need', 'At-need'], ['pre_need', 'Pre-need']]}
            />
            <div className="flex items-start gap-3">
              <Field
                label={dealStage === 'at_need' ? 'Timeline *' : 'Timeline'}
                htmlFor="q-timeline"
                error={errors.timeline}
              >
                <select
                  id="q-timeline"
                  value={timeline}
                  onChange={(e) => {
                    setTimeline(e.target.value);
                    setErrors((er) => ({ ...er, timeline: undefined }));
                  }}
                  className={cn(selectClass, errors.timeline && 'border-destructive')}
                >
                  <option value="">Select…</option>
                  {timelineOptions.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </Field>
              {dealStage === 'at_need' && (
                <Field label="Relationship" htmlFor="q-rel">
                  <select
                    id="q-rel"
                    value={relationship}
                    onChange={(e) => setRelationship(e.target.value)}
                    className={selectClass}
                  >
                    <option value="">Select…</option>
                    {RELATIONSHIPS.map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </Field>
              )}
            </div>
          </section>

          <section className="grid gap-2.5">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Follow-up</h3>

            <div className={cn('grid gap-2 rounded-lg border p-3', !(sendEmail && email.trim()) && 'bg-muted/50')}>
              <div className="flex items-center gap-2.5">
                <input
                  type="checkbox"
                  id="q-send-email"
                  checked={sendEmail}
                  disabled={!email.trim()}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  className="h-4 w-4 accent-primary"
                />
                <label htmlFor="q-send-email" className="text-sm font-semibold">Send the email</label>
                <span className="min-w-0 truncate text-[13px] text-muted-foreground">
                  {email.trim() ? `to ${email.trim()}` : 'No email on file'}
                </span>
                <span className="flex-1" />
                {sendEmail && email.trim() && (
                  <button
                    type="button"
                    className="text-[13px] text-primary hover:underline"
                    aria-expanded={emailOpen}
                    onClick={() => setEmailOpen((o) => !o)}
                  >
                    {emailOpen ? 'Hide preview' : 'Preview & edit'}
                  </button>
                )}
              </div>
              {sendEmail && email.trim() && emailOpen && (
                <div className="grid gap-2">
                  {previewLoading && <span className="text-xs text-muted-foreground">Loading template…</span>}
                  {previewError && (
                    <span className="text-xs text-destructive">
                      {previewError}. The default template will be sent as is.
                    </span>
                  )}
                  {!previewLoading && !previewError && (emailSubject || emailHtml) && (
                    <>
                      <Field label="Subject" htmlFor="q-subject">
                        <Input id="q-subject" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
                      </Field>
                      <EmailHtmlEditor html={emailHtml} onChange={setEmailHtml} />
                      <span className="text-xs text-muted-foreground">The quote link is added when you qualify.</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {smsShown && (
              <div className={cn('grid gap-2 rounded-lg border p-3', !(smsAvailable && sendSms) && 'bg-muted/50')}>
                <div className="flex items-center gap-2.5">
                  <input
                    type="checkbox"
                    id="q-send-sms"
                    checked={smsAvailable && sendSms}
                    disabled={!smsAvailable}
                    onChange={(e) => setSendSms(e.target.checked)}
                    className="h-4 w-4 accent-primary"
                  />
                  <label htmlFor="q-send-sms" className="text-sm font-semibold">Send a text</label>
                  {sms?.phone && (
                    <span className="text-[13px] text-muted-foreground">to {formatPhone(sms.phone)}</span>
                  )}
                </div>
                {smsAvailable ? (
                  sendSms && (
                    <div className="grid gap-1.5">
                      <Textarea
                        aria-label="Text message"
                        rows={Math.max(5, smsText.split('\n').length + 3)}
                        value={smsText}
                        onChange={(e) => {
                          smsEditedRef.current = true;
                          setSmsText(e.target.value);
                        }}
                      />
                      <span className="text-[13px] font-medium text-primary">{sms?.hint}</span>
                    </div>
                  )
                ) : (
                  <span className="text-[13px] text-muted-foreground">
                    {sms?.phone
                      ? sms.disabledReason
                      : 'No mobile number on file. Add one to the person to send a text.'}
                  </span>
                )}
              </div>
            )}
          </section>
        </div>

        <div className="flex justify-end gap-2 border-t px-5 py-3">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Qualifying…' : 'Qualify'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
